import type { ReactElement } from 'react';
import { useEffect } from 'react';
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  useRouter,
} from '@tanstack/react-router';
import { useAuthMode, useAuthStatus } from '@/lib/auth';
import { AuthLayout } from '@/features/layout/AuthLayout';
import { AppLayout } from '@/features/layout/AppLayout';
import { SetupWizard } from '@/features/auth/SetupWizard';
import { LoginForm } from '@/features/auth/LoginForm';
import { LoginFormMultiUser } from '@/features/auth/LoginFormMultiUser';
import { SignupForm } from '@/features/auth/SignupForm';
import { PasswordResetRequestForm } from '@/features/auth/PasswordResetRequestForm';
import { PasswordResetConfirmForm } from '@/features/auth/PasswordResetConfirmForm';
import { Dashboard } from '@/features/dashboard/Dashboard';
import { ProfileView } from '@/features/profile/ProfileView';
import { WeeklyPreferencesForm } from '@/features/profile/WeeklyPreferencesForm';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { StravaSettings } from '@/features/settings/StravaSettings';
import { AnthropicSettings } from '@/features/settings/AnthropicSettings';
import { GoogleSettings } from '@/features/settings/GoogleSettings';
import { CoachPage } from '@/features/coach/CoachPage';
import { LoadPage } from '@/features/load/LoadPage';
import { SeasonPage } from '@/features/season/SeasonPage';
import { LibraryPage } from '@/features/library/LibraryPage';
import { NutritionPage } from '@/features/nutrition/NutritionPage';
import { WorkoutDetail } from '@/features/workouts/WorkoutDetail';
import { WorkoutsPage } from '@/features/workouts/WorkoutsPage';
import { AnalyticsPage } from '@/features/analytics/AnalyticsPage';

/**
 * Router code-based de la app.
 *
 * Estrategia de auth-gating:
 * - El root carga el estado de auth (`useAuthStatus`) y, según el resultado y la
 *   ruta actual, redirige imperativamente vía `router.navigate`. Hacerlo en el
 *   root evita duplicar la lógica en cada ruta y mantiene los `loader`s libres
 *   (que aún no usamos para no acoplarnos al QueryClient desde fuera del React
 *   tree).
 * - Mientras la query está cargando, se muestra un placeholder neutral. Una vez
 *   resuelto, las redirecciones son síncronas en useEffect.
 *
 * M2.42 — coexistencia single-user / multi-user:
 * - `useAuthMode()` se carga al inicio (no requiere auth) y dice si la
 *   instalación está en modo multi-usuario.
 * - En single-user: comportamiento original — sin setup → /setup, sin sesión
 *   → /login (passphrase).
 * - En multi-user: `/setup` no aplica (el primer usuario se crea con `/signup`).
 *   Las rutas `/signup`, `/login`, `/password-reset/*` son públicas; el resto
 *   requiere autenticación y redirige a `/login` si falta.
 * - `/login` muestra `LoginForm` (passphrase) en single-user y
 *   `LoginFormMultiUser` (email+password) en multi-user. La ruta es la misma
 *   en ambos modos para mantener bookmarks/UX estables.
 */

const PUBLIC_MULTI_USER_PATHS = [
  '/login',
  '/signup',
  '/password-reset/request',
  '/password-reset/confirm',
] as const;

function isPublicMultiUserPath(path: string): boolean {
  return (PUBLIC_MULTI_USER_PATHS as readonly string[]).includes(path);
}

function RootComponent(): ReactElement {
  const status = useAuthStatus();
  const mode = useAuthMode();
  const router = useRouter();
  const currentPath = router.state.location.pathname;

  useEffect(() => {
    if (!status.data || !mode.data) return;
    const { initialized, authenticated } = status.data;
    const multiUser = mode.data.multiUser;

    if (multiUser) {
      // En multi-user no usamos /setup — el primer usuario se da de alta
      // como cualquier otro vía /signup. `initialized` es siempre `true`
      // del lado del backend (no hay onboarding global).
      if (!authenticated && !isPublicMultiUserPath(currentPath)) {
        router.navigate({ to: '/login', replace: true });
        return;
      }
      if (authenticated && isPublicMultiUserPath(currentPath)) {
        router.navigate({ to: '/', replace: true });
      }
      return;
    }

    // Single-user — flow original (passphrase).
    if (!initialized && currentPath !== '/setup') {
      router.navigate({ to: '/setup', replace: true });
      return;
    }
    if (initialized && !authenticated && currentPath !== '/login') {
      router.navigate({ to: '/login', replace: true });
      return;
    }
    if (authenticated && (currentPath === '/setup' || currentPath === '/login')) {
      router.navigate({ to: '/', replace: true });
    }
  }, [status.data, mode.data, currentPath, router]);

  if (status.isLoading || mode.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 text-slate-700">
        <p role="status" aria-live="polite">
          Cargando…
        </p>
      </div>
    );
  }

  if (status.isError || mode.isError) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-6 text-slate-700">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-semibold text-slate-900">No se puede contactar con el backend</h1>
          <p className="mt-2 text-sm text-slate-600">
            Asegúrate de que el servicio está corriendo y recarga la página.
          </p>
        </div>
      </div>
    );
  }

  return <Outlet />;
}

/**
 * Componente del `/login` que selecciona el form según el modo de auth.
 * Se mantiene la ruta `/login` única para que los enlaces (incluido el
 * fallback de gating) funcionen sin condicional en cada sitio.
 */
function LoginRouteComponent(): ReactElement {
  const mode = useAuthMode();
  // El RootComponent ya garantiza que `mode.data` está resuelto antes de
  // pintar las rutas hijas, pero por defensa fallback a passphrase.
  const multiUser = mode.data?.multiUser ?? false;
  return (
    <AuthLayout>
      {multiUser ? <LoginFormMultiUser /> : <LoginForm />}
    </AuthLayout>
  );
}

/**
 * Construye un nuevo árbol de rutas. Se usa una factoría (en lugar de exportar
 * rutas singleton) porque `rootRoute.addChildren(...)` muta el rootRoute, y los
 * tests con history en memoria necesitan árboles independientes.
 */
export function buildRouteTree() {
  const rootRoute = createRootRoute({ component: RootComponent });

  const setupRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/setup',
    component: function SetupRouteComponent() {
      return (
        <AuthLayout>
          <SetupWizard />
        </AuthLayout>
      );
    },
  });

  const loginRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/login',
    component: LoginRouteComponent,
  });

  // M2.42 — rutas multi-user. Existen siempre (registradas en el árbol) pero
  // el gating del RootComponent solo deja llegar a ellas en modo multi-user.
  // Si un usuario navega a /signup en single-user, el RootComponent lo
  // redirige a /setup o /login según `initialized`.
  const signupRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/signup',
    component: function SignupRouteComponent() {
      return (
        <AuthLayout>
          <SignupForm />
        </AuthLayout>
      );
    },
  });

  const passwordResetRequestRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/password-reset/request',
    component: function PasswordResetRequestRouteComponent() {
      return (
        <AuthLayout>
          <PasswordResetRequestForm />
        </AuthLayout>
      );
    },
  });

  // `validateSearch` mantiene `token` opcional. La validación dura del token
  // (presencia, formato, no caducado) la hace el backend; aquí no
  // bloqueamos render si falta — el form muestra un mensaje y enlace.
  const passwordResetConfirmRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/password-reset/confirm',
    validateSearch: (search: Record<string, unknown>): { token?: string } => {
      const t = search.token;
      return typeof t === 'string' ? { token: t } : {};
    },
    component: function PasswordResetConfirmRouteComponent() {
      return (
        <AuthLayout>
          <PasswordResetConfirmForm />
        </AuthLayout>
      );
    },
  });

  const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    component: function IndexRouteComponent() {
      return (
        <AppLayout>
          <Dashboard />
        </AppLayout>
      );
    },
  });

  const profileRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/perfil',
    component: function ProfileRouteComponent() {
      return (
        <AppLayout>
          <ProfileView />
        </AppLayout>
      );
    },
  });

  /**
   * `/perfil/semana` — preferencias semanales (M2.31.A).
   * Página dedicada en lugar de embebida en `/perfil` porque combina
   * 7 cards + sección de automatización; sería demasiado denso en la
   * vista de perfil.
   */
  const profileWeekRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/perfil/semana',
    component: function ProfileWeekRouteComponent() {
      return (
        <AppLayout>
          <WeeklyPreferencesForm />
        </AppLayout>
      );
    },
  });

  const settingsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/ajustes',
    component: function SettingsRouteComponent() {
      return (
        <AppLayout>
          <SettingsPage />
        </AppLayout>
      );
    },
  });

  const stravaSettingsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/ajustes/strava',
    component: function StravaSettingsRouteComponent() {
      return (
        <AppLayout>
          <StravaSettings />
        </AppLayout>
      );
    },
  });

  const anthropicSettingsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/ajustes/anthropic',
    component: function AnthropicSettingsRouteComponent() {
      return (
        <AppLayout>
          <AnthropicSettings />
        </AppLayout>
      );
    },
  });

  const googleSettingsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/ajustes/google',
    component: function GoogleSettingsRouteComponent() {
      return (
        <AppLayout>
          <GoogleSettings />
        </AppLayout>
      );
    },
  });

  const coachNewPlanRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/coach/nuevo-plan',
    component: function CoachNewPlanRouteComponent() {
      return (
        <AppLayout>
          <CoachPage />
        </AppLayout>
      );
    },
  });

  const loadRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/carga',
    component: function LoadRouteComponent() {
      return (
        <AppLayout>
          <LoadPage />
        </AppLayout>
      );
    },
  });

  const seasonRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/coach/temporada',
    component: function SeasonRouteComponent() {
      return (
        <AppLayout>
          <SeasonPage />
        </AppLayout>
      );
    },
  });

  const libraryRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/biblioteca',
    component: function LibraryRouteComponent() {
      return (
        <AppLayout>
          <LibraryPage />
        </AppLayout>
      );
    },
  });

  /**
   * `/entreno/$id` — vista detalle de un actual_workout.
   * Validamos `id` a número en cliente; si llega un valor no numérico
   * (URL manipulada), pasamos `NaN` que el hook bloqueará con `enabled:false`.
   */
  const workoutDetailRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/entreno/$id',
    component: function WorkoutDetailRouteComponent() {
      const params = workoutDetailRoute.useParams();
      const id = Number(params.id);
      return (
        <AppLayout>
          <WorkoutDetail id={Number.isFinite(id) ? id : NaN} />
        </AppLayout>
      );
    },
  });

  /**
   * `/analiticas` — análisis profundo (M2.13). El query param `?tab=` lo
   * lee el componente `AnalyticsPage`; no usamos `validateSearch` para no
   * bloquear cuando llegan otros params.
   */
  const analyticsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/analiticas',
    component: function AnalyticsRouteComponent() {
      return (
        <AppLayout>
          <AnalyticsPage />
        </AppLayout>
      );
    },
  });

  /**
   * `/entrenos` — calendario de actividades (M2.19). Vistas Día/Semana/Mes
   * y la antigua Lista (filtros + tabla) están encapsuladas en
   * `WorkoutsPage`. Estado de vista/cursor es local al componente.
   */
  const workoutsListRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/entrenos',
    component: function WorkoutsRouteComponent() {
      return (
        <AppLayout>
          <WorkoutsPage />
        </AppLayout>
      );
    },
  });

  const nutritionRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/nutricion',
    // `?tab=` se lee desde `router.state.location.search` dentro de
    // `NutritionPage`; no añadimos `validateSearch` para no bloquear
    // navegaciones cuando se llega con otros params (p. ej. `?recipe=`).
    component: function NutritionRouteComponent() {
      return (
        <AppLayout>
          <NutritionPage />
        </AppLayout>
      );
    },
  });

  return rootRoute.addChildren([
    setupRoute,
    loginRoute,
    signupRoute,
    passwordResetRequestRoute,
    passwordResetConfirmRoute,
    indexRoute,
    profileRoute,
    profileWeekRoute,
    settingsRoute,
    stravaSettingsRoute,
    anthropicSettingsRoute,
    googleSettingsRoute,
    coachNewPlanRoute,
    loadRoute,
    seasonRoute,
    libraryRoute,
    nutritionRoute,
    analyticsRoute,
    workoutsListRoute,
    workoutDetailRoute,
  ]);
}

export const router = createRouter({
  routeTree: buildRouteTree(),
  defaultPreload: false,
});

// Type augmentation para que `Link to="/..."` sea tipado.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
